import { defineStore } from 'pinia'
import { api } from '@/service/api'
import { ref } from 'vue'
import { useIndexerStore } from './indexerStore'
import { useWebsocketStore } from './websocketStore'
import { useRouter } from 'vue-router'
import type {
  UserContactRequest,
  UserContactResponse,
  UserInfoResponse,
  UserListRequest,
  UserListResponse
} from '@/interface/gen/request'
import { useContactStore } from './contactStore'
import type { UserRoles } from '@/interface/gen/db'

export const useUserStore = defineStore('userStore', () => {
  const indexerStore = useIndexerStore()
  const contactStore = useContactStore()
  const websocketStore = useWebsocketStore()
  const router = useRouter()

  const me = ref<UserInfoResponse | null>(null)
  const inited = ref(false)

  async function initUser(withStores: boolean = true) {
    try {
      const res = await userInfo()
      me.value = res
      if (withStores) {
        initAuthorizedStores(res.user_id)
      }
    } catch {
      me.value = null
    } finally {
      inited.value = true
      if (!me.value?.user_id) {
        contactStore.closeContactModal()
        return
      }

      if (!me.value?.has_phone) {
        contactStore.openContactModal()
        return
      }

      contactStore.closeContactModal()
    }
  }

  function hasRole(userInfo: UserInfoResponse | null, role: UserRoles) {
    return userInfo
      ? userInfo.user_roles.some((r: UserRoles) => r === role)
      : false
  }

  async function userInfo(): Promise<UserInfoResponse> {
    return await api.get('/api/private/v1/user/me')
  }

  function clearUser() {
    me.value = null
    inited.value = true
  }

  function initAuthorizedStores(userId: number) {
    indexerStore.updateToken()
    websocketStore.setupClient(userId)
  }

  async function logout(invalidateCookie: boolean = true) {
    clearUser()

    if (invalidateCookie) {
      await api.post('/api/private/v1/auth/logout')
    }

    if (router.currentRoute.value.name !== 'auth') {
      router.replace({ name: 'auth' })
    }
  }

  async function usersList(req: UserListRequest): Promise<UserListResponse> {
    return await api.get('/api/private/v1/user/list', req as {})
  }

  async function getUserContact(req: UserContactRequest): Promise<UserContactResponse> {
    return await api.get('/api/private/v1/user/contact/get', req as {})
  }

  return {
    me,
    userInfo,
    initUser,
    inited,
    clearUser,
    logout,
    usersList,
    getUserContact,
    hasRole
  }
})
